import { config } from '@config';
import { timer } from 'rxjs';
import { history, RxUtils, SubscriptionList } from '@utils';
import { concatMap, tap, map, filter } from 'rxjs/operators';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { getStore, RootState } from '@store';
import { setLanguage, setLoggedinUser, setTokens } from '@store/slices/common.slice';
import { ITokens } from './tokens.interface';
import { setAssumedOrganization, setAssumedRole } from '@store/slices/portal.slice';
import { ResetPasswordRequest } from './reset-password-request.class';

const TOKEN_VALIDITY_CHECK_INTERVAL = 60000;

export class AuthService {
	private subscriptions: SubscriptionList & { [key: string]: any } = new SubscriptionList();
	private store = getStore().store;
	private getUrl = () => `${config.apiUrl}/auth`;

	constructor() {
		this.startTokenValidityChecker();
	}

	login(email: string, password: string) {
		return httpService
			.post<ITokens>(this.getUrl(), 'login', { body: { email, password }, noAuth: true, errorHandler: 'notification' })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.login.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
				map((tokens) => this.store.dispatch(setTokens(tokens)).payload),
				map((tokens) => this.store.dispatch(setLoggedinUser({ email: tokens?.idToken.payload.email })).payload)
			);
	}

	login2(organizationId: string, data: string) {
		return httpService
			.post<ITokens>(this.getUrl(), 'login2', {
				body: { organizationId, encrypted: data },
				noAuth: true,
				errorHandler: 'notification',
			})
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.login2.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
				concatMap((result) => RxUtils.mapValueOrThrow(result?.idToken?.jwtToken, new Error('Response is not valid'), result)),
				map((tokens) => this.store.dispatch(setTokens(tokens)).payload),
				map((tokens) => this.store.dispatch(setLoggedinUser({ email: tokens?.idToken.payload.email })).payload)
			);
	}

	refresh(disableLoader = true) {
		const token = (this.store.getState() as RootState).Common.tokens?.refreshToken?.token;
		return httpService.post<ITokens>(this.getUrl(), 'token-refresh', { body: { token }, disableLoader }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.refresh.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty'))),
			map((tokens) => this.store.dispatch(setTokens(tokens)))
		);
	}

	forgotPassword(email: string) {
		return httpService
			.post<unknown>(this.getUrl(), 'forgot-password', { body: { email }, noAuth: true, errorHandler: 'notification' })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.forgotPassword.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	resetPassword(email: string, code: string, password: string) {
		return httpService
			.post<unknown>(this.getUrl(), 'confirm-password', {
				body: { email, code: `${code}`, password },
				noAuth: true,
				errorHandler: 'notification',
			})
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.resetPassword.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	resetPasswordByAdmin(body: ResetPasswordRequest) {
		return httpService
			.post<ResetPasswordRequest>(this.getUrl(), 'reset', {
				body,
				errorHandler: 'notification',
			})
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.resetPasswordByAdmin.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	changePassword(email: string, oldPassword: string, newPassword: string) {
		return httpService
			.post<unknown>(this.getUrl(), 'force-change-password', {
				body: { email, oldPassword, newPassword },
				noAuth: true,
				errorHandler: 'notification',
			})
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.changePassword.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	confirmRegistration(email: string, code: string) {
		return httpService
			.post<unknown>(this.getUrl(), 'confirm-registration', { body: { email, code }, noAuth: true, errorHandler: 'notification' })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.confirmRegistration.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	resendConfirmationCode(email: string) {
		return httpService
			.post<unknown>(this.getUrl(), `resend-confirmation-code/${email}`, { noAuth: true, errorHandler: 'notification' })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.resendConfirmationCode.name}]`, result)),
				concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
			);
	}

	logout() {
		this.store.dispatch(setLanguage(null));
		this.store.dispatch(setTokens(null));
		this.store.dispatch(setAssumedOrganization(null));
		this.store.dispatch(setAssumedRole(null));
		return Promise.resolve(true);
	}

	isAdmin() {
		const cognitoGroups = (this.store.getState() as RootState).Common.tokens?.accessToken?.payload['cognito:groups'];
		if (Array.isArray(cognitoGroups)) {
			return cognitoGroups.map((group: string) => group.toLowerCase()).includes('admin');
		}
		return false;
	}

	isTokenExpired(returnValueIfTokenNotExist = false): boolean {
		const token = (this.store.getState() as RootState).Common.tokens?.accessToken?.payload;
		if (token) {
			const exp = token.exp * 1000;
			const diff = exp - Date.now();
			return diff < 5 * TOKEN_VALIDITY_CHECK_INTERVAL;
		}
		return returnValueIfTokenNotExist;
	}

	private startTokenValidityChecker() {
		const pipename = 'TokenValidtyChecker';
		this.subscriptions[pipename] = timer(0, TOKEN_VALIDITY_CHECK_INTERVAL)
			.pipe(
				filter(() => this.isTokenExpired()),
				concatMap(() => this.refresh())
			)
			.subscribe({
				error: () => this.logout().then(() => history.push('/login')),
			});
	}
}

export const authService = new AuthService();
